/**
 * 會員管理頁面
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Search, Mail, Calendar, Shield, User,
  ChevronLeft, ChevronRight
} from 'lucide-react';

export function MemberManagement() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalMembers: 0,
    adminCount: 0,
    memberCount: 0,
  });

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (!isAdmin()) {
        navigate('/member');
      }
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (user && isAdmin()) {
      fetchMembers();
    }
  }, [user]);

  const fetchMembers = async () => {
    try {
      // 從 Supabase Auth 查詢所有用戶
      const { data, error } = await supabase.auth.admin.listUsers();

      if (error) throw error;

      const users = data?.users || [];
      setMembers(users);

      // 計算統計
      const adminCount = users.filter(u => u.user_metadata?.role === 'admin').length;
      setStats({
        totalMembers: users.length,
        adminCount,
        memberCount: users.length - adminCount,
      });
    } catch (error) {
      console.error('查詢會員失敗:', error);
      // 如果沒有 admin 權限，使用替代方案
      alert('注意：需要 Supabase Service Role Key 才能查看所有會員');
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(member => 
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.user_metadata?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* 返回按鈕 */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          返回後台管理
        </Button>

        {/* 頁面標題 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">會員管理</h1>
          <p className="text-gray-600">查看和管理所有註冊會員</p>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-blue-100">總會員數</CardDescription>
              <CardTitle className="text-3xl">{stats.totalMembers}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-blue-100">
                <Users className="h-4 w-4" />
                <span className="text-sm">所有註冊會員</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-purple-100">管理員</CardDescription>
              <CardTitle className="text-3xl">{stats.adminCount}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-purple-100">
                <Shield className="h-4 w-4" />
                <span className="text-sm">系統管理員</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-green-100">一般會員</CardDescription>
              <CardTitle className="text-3xl">{stats.memberCount}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-green-100">
                <User className="h-4 w-4" />
                <span className="text-sm">一般用戶</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 會員列表 */}
        <Card className="bg-white/80 backdrop-blur-xl border-white/20 shadow-lg">
          <CardHeader>
            <CardTitle>會員列表</CardTitle>
            <CardDescription>所有註冊會員資訊</CardDescription>
            
            {/* 搜尋欄 */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="搜尋會員 Email 或姓名..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchTerm ? '沒有符合的會員' : '尚無會員'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">姓名</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">手機</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">角色</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">Email驗證</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">註冊時間</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((member) => (
                      <tr key={member.id} className="border-b hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {member.user_metadata?.name?.[0] || member.email[0].toUpperCase()}
                            </div>
                            <span className="font-medium">
                              {member.user_metadata?.name || '未設定'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{member.email}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {member.user_metadata?.phone || '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge 
                            className={member.user_metadata?.role === 'admin' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-green-100 text-green-800'
                            }
                          >
                            {member.user_metadata?.role === 'admin' ? '管理員' : '會員'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={member.email_confirmed_at 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                          }>
                            {member.email_confirmed_at ? '已驗證' : '未驗證'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center text-sm text-gray-600">
                          <div className="flex items-center justify-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {new Date(member.created_at).toLocaleDateString('zh-TW')}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* 提示訊息 */}
            {!loading && members.length === 0 && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>提示：</strong>需要在 Cloudflare Functions 中使用 
                  <code className="mx-1 px-2 py-1 bg-yellow-100 rounded">SUPABASE_SERVICE_ROLE_KEY</code> 
                  才能查詢所有會員資料。
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MemberManagement;
